import React from 'react';
import t from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  DashboardSection,
  Header3,
  Header5,
} from 'fiducius-ui';

import { safeAccess, mergeRequestStatuses, formatAsMoney } from '../../../utils';

import { getUserId, authLoadPermissions } from '../../../auth';
import { withPermissions } from '../../../routing';

import RenewalConfirmation from '../components/renewal-confirmation';
import ForgivenessProgress from '../components/forgiveness-progress';
import { renewalResultsLoadResource, renewalResultsUpdateResource } from '../../../renewal-results';
import DocsNeededForRSUpload from '../components/docs-upload-needed';
import {
  getForgivenessData,
  getTotalForgiveness,
  getMonthlySavings,
  getPastYearSavings,
  getCurrentYearSavings,
  getSAVEPlanSavings,
} from '../redux/selectors';
import { forgivenessLoadResource } from '../redux/operations';

const StyledDivCentered = styled.div`
  text-align: center;
`;

const Margin = styled.div`
  margin: 0px 200px 0px 200px;
`;

const CenteredTable = styled.table`
  width: 100%;
  height: 100%;
`;

class FeeRS extends React.Component {
  static propTypes = {
    request: t.object.isRequired,
    permissions: t.object.isRequired,
    loadForgiveness: t.func.isRequired,
    pastYearSavings: t.object,
    currentYearSavings: t.object,
    saveProgramSavings: t.object,
    forgivenessData: t.object,
    data: t.object,
    loadData: t.func.isRequired,
    acknowledgeRsFeeCredits: t.func.isRequired,
    id: t.string,
    totalForgiveness: t.number,
    monthlySavings: t.number,
  };

  constructor(props) {
    super(props);
    this.state = { feePaid: false };
  }

  componentDidMount() {
    this.props.loadData(this.props.id);
    this.setState({ feePaid: false });
    const { id, loadForgiveness, forgivenessData, permissions } = this.props;
    if (Object.keys(forgivenessData).length == 0) {
      loadForgiveness(id);
    }
  }

  componentDidUpdate() {
    if (
      this.state.feePaid !== this.props.data.paymentReceived &&
      this.props.data.paymentReceived !== undefined
    ) {
      this.setState({ feePaid: true });
      this.props.loadData(this.props.id);
    }
  }

  acknowledgeFee = async () => {
    await this.props.acknowledgeRsFeeCredits(this.props.id);
    this.props.loadData(this.props.id);
  };

  getFeeText = () => {
    const {
      data,
      pastYearSavings,
      currentYearSavings,
      saveProgramSavings,
      forgivenessData,
      monthlySavings,
      totalForgiveness,
    } = this.props;
    if (data !== undefined && data !== null) {
      if (
        data.isOriginationSubscription ||
        data.isReducedIdrImplementation ||
        data.isReducedIdrRenewal
      ) {
        return (
          <>
            <Margin>
              <StyledDivCentered>
                <p>
                  <Header3>
                    <b>Secure Your Financial Future with Student Loan 365</b>
                  </Header3>
                </p>
              </StyledDivCentered>
              <StyledDivCentered>
                <p>
                  <Header5>
                    <i>Total Support. Real Forgiveness. Zero guesswork.</i>
                  </Header5>
                </p>
              </StyledDivCentered>
              <StyledDivCentered>
                <p>
                  Our new all-inclusive subscription model works to deliver powerful financial
                  advantages specific to your needs. Whether you are chasing forgiveness, struggling
                  with payments, planning for your children's future, or simply trying to keep up,
                  Student Loan 365 has you covered.
                </p>
              </StyledDivCentered>
              <p>
                <b>
                  <u>What You Get</u>
                </b>
              </p>
              <p>
                <ul>
                  <li>
                    <b>Expert Guidance, On-Demand</b>
                    <br />
                    Tools, experts, and resources available at your fingertips 24 hours per day, 7
                    days per week, 365 days per year.
                  </li>
                </ul>
              </p>
              <p>
                <ul>
                  <li>
                    <b>Premium Content Built for Real Results</b>
                    <br />
                    Step-by-step guidance, customized insights, and exclusive financial wellness
                    tools engineered to address your specific needs.
                  </li>
                </ul>
              </p>
              <p>
                <ul>
                  <li>
                    <b>Unlimited Application Submission Support</b>
                    <br />
                    Life changes and with those changes come opportunities for additional savings
                    throughout the year. When those changes arise, you'll have the ability to
                    generate and submit an unlimited number of applications to take advantage of new
                    benefits.
                  </li>
                </ul>
              </p>
              <p>
                <ul>
                  <li>
                    <b>Advanced Forgiveness Compliance Measures</b>
                    <br />
                    Don't miss a beat or stress about forgiveness rules. We keep your account up to
                    date with the newest policy changes to keep you on track.
                  </li>
                </ul>
              </p>
              <p>
                <ul>
                  <li>
                    <b>Short Term Relief with Long Term Impact</b>
                    <br />
                    Lower your monthly payments and maximize your forgiveness. Free up the income
                    for what matters most – your family's future.
                  </li>
                </ul>
              </p>

              <StyledDivCentered>
                <p>
                  <Header3>
                    <b>Take a quick look at what financial impact you can expect:</b>
                  </Header3>
                </p>
              </StyledDivCentered>

              <StyledDivCentered>
                <CenteredTable>
                  <tr>
                    {monthlySavings > 0 && (
                      <td>
                        <b>Monthly Payment Reduction</b>
                      </td>
                    )}
                    {totalForgiveness > 0 && (
                      <td>
                        <b>Projected Loan Forgiveness</b>
                      </td>
                    )}
                  </tr>
                  <tr>
                    {monthlySavings > 0 && <td>{formatAsMoney(monthlySavings)}</td>}
                    {totalForgiveness > 0 && <td>{formatAsMoney(totalForgiveness)}</td>}
                  </tr>
                </CenteredTable>
                <br />
                <br />
              </StyledDivCentered>

              <StyledDivCentered>
                <p>
                  <b>Don't Navigate the Chaos Alone – Focus Your Time on What Truly Matters!</b>
                </p>
              </StyledDivCentered>

              <StyledDivCentered>
                <p>
                  <b>Start Now. Save More. Stress Less.</b>
                </p>
              </StyledDivCentered>
            </Margin>
          </>
        );
      } else if (data.renewalBalance > 0 && !data.paymentReceived) {
        const showSavingsText = pastYearSavings > 0 && currentYearSavings > 0;
        const showSAVESavingsText = saveProgramSavings > 0;
        const showIncreaseText = forgivenessData.islsIdrPayment > 0;
        let savingsAmountText =
          'We anticipate you could save approximately ' +
          formatAsMoney(saveProgramSavings) +
          ' under the new plan.';

        return (
          <Header5>
            <p>The next step of the renewal process is to pay your renewal fee.</p>
            {showSavingsText && !showSAVESavingsText && (
              <p>
                You had estimated savings of approximately {formatAsMoney(pastYearSavings)} this
                past year and we anticipate you'll save {formatAsMoney(currentYearSavings)} after
                completing your renewal for this year.
              </p>
            )}
            {showSavingsText && showSAVESavingsText && (
              <p>
                You had estimated savings of approximately {formatAsMoney(pastYearSavings)} this
                past year and we anticipate you'll save {formatAsMoney(currentYearSavings)} after
                completing your renewal for this year. And with the recent announcement of the SAVE
                Repayment plan, your savings could increase! {showIncreaseText && savingsAmountText}
              </p>
            )}
            <p>{!data.selfService && <DocsNeededForRSUpload documentButton={true} />}</p>
          </Header5>
        );
      } else if (data.hasEmployerPaidRenewal) {
        return (
          <Header5>
            <p>Your renewal fee is paid for by your employer.</p>
          </Header5>
        );
      } else if (data.renewalBalance === 0 && !data.paymentReceived) {
        return (
          <Header5>
            <p>
              Your renewal fee of {formatAsMoney(data.serviceFee)} is paid in full due to renewal
              credits applied to your account
            </p>
          </Header5>
        );
      } else if (data.paymentReceived) {
        return (
          <Header5>
            <p>
              Your renewal fee of {formatAsMoney(data.serviceFee)} has been set up and received.
            </p>
          </Header5>
        );
      }
    }
  };

  render() {
    const { data } = this.props;
    let renewalConfirmationStepId = 250;
    let showProgressComponent = false;

    if (data.isReducedIdrRenewal) {
      renewalConfirmationStepId = 1150;
    } else if (data.isReducedIdrImplementation) {
      renewalConfirmationStepId = 1550;
    } else if (data.isOriginationSubscription) {
      renewalConfirmationStepId = 2200;
    }

    let cardTitleText = '';
    if (data !== undefined && data !== null && Object.keys(data).length > 0) {
      if (
        renewalConfirmationStepId === 1150 ||
        renewalConfirmationStepId === 1550 ||
        renewalConfirmationStepId === 2200
      ) {
        cardTitleText = '';
        showProgressComponent = false;
      } else {
        cardTitleText = 'Renewal Fee';
        showProgressComponent = true;
      }
    }

    return (
      <>
        {showProgressComponent && <ForgivenessProgress />}
        <DashboardSection>
          <Card brand="primary">
            <CardBody>
              <CardTitle>{cardTitleText}</CardTitle>
              <CardText>{data && this.getFeeText()}</CardText>
            </CardBody>
            <RenewalConfirmation
              stepId={renewalConfirmationStepId}
              zeroBalance={data && data.renewalBalance === 0 && !data.paymentReceived}
            />
          </Card>
        </DashboardSection>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    id: getUserId(state),
    data: safeAccess(state, `renewalResults.cache[${getUserId(state)}]`) || {},
    request: mergeRequestStatuses([
      state.renewalResults.requests.loadResource,
      state.renewalResults.requests.updateResource,
    ]),
    forgivenessData: getForgivenessData(state),
    pastYearSavings: getPastYearSavings(state),
    currentYearSavings: getCurrentYearSavings(state),
    saveProgramSavings: getSAVEPlanSavings(state),
    monthlySavings: getMonthlySavings(state),
    totalForgiveness: getTotalForgiveness(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  loadData: async (id) => {
    dispatch(renewalResultsLoadResource(id));
  },
  acknowledgeRsFeeCredits: async (id) => {
    await dispatch(renewalResultsUpdateResource(id));
    dispatch(authLoadPermissions());
  },
  loadForgiveness: (id) => dispatch(forgivenessLoadResource(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withPermissions(FeeRS));
